import React, { Fragment } from 'react';
import { css, StyleSheet } from 'aphrodite';

import { Helper } from '../../common/helpers/Helper';
import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { AppTheme } from '../../styles/AppTheme';
import Grid from '@material-ui/core/Grid';
import { Stats } from '../../components/Stats';
import { VenueSummary } from '../../components/VenueSummary';
import { ApiHelper } from '../../common/helpers/ApiHelper';
import { UiHelper } from '../../common/helpers/UiHelper';
import { Doughnut } from 'react-chartjs-2';
import { AppHelper } from '../../helpers/AppHelper';
import moment from 'moment-timezone';
import { SearchBar } from '../../common/components/SearchBar';
import { DynamicFormDialog } from '../../common/components/DynamicFormDialog';
import { GraphHelper } from '../../common/components/charts/GraphHelper';
import { screenPerformanceFilterFieldDef } from '../../data/data';
import { AppConfig } from '../../AppConfig';

const queryString = require('query-string');

const getDefaultFilters = () => {
  const previousCstDay = moment().tz(AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO).subtract(1, 'day').startOf('day');

  return {
    startDate: previousCstDay.clone().subtract(6, 'days').valueOf(),
    endDate: previousCstDay.valueOf(),
    partner: '',
    site: ''
  };
}

const getScreenFromLocation = (search = '') => {
  const query = queryString.parse(search);
  const screen = query.screen || '';
  return String(screen || '').trim();
}

export class ScreenPerformanceDashboard extends BaseEntityPage {
  pageKey = 'ScreenPerformanceDashboard';
  pageTitle = Helper.getString('ScreenPerformanceDashboard');
  orgId = this.props.match.params.orgId || 'PRODUCT';
  breadCrumbs = [
    {
      title: Helper.getString('home'),
      to: this.baseRoute()
    },
    {
      title: Helper.getString('ScreenPerformanceDashboard')
    }
  ];

  state = {
    showFilterDialog: false,
    filters: {},
    statsTotal: {},
    statsDay: {},
    tableData: []
  };

  constructor(props, options) {
    super(props, options);

    this.noAdd = true;
    this.noTableHead = false;

    this.query = queryString.parse(props.location.search);
    const initialScreen = getScreenFromLocation(props.location.search);

    this.state = {
      ...this.state,
      searchText: initialScreen,
      inProcessSearch: false,
      filters: getDefaultFilters()
    };

    this.hasLoadedOrgSearch = false;
    this.toFetch = this.makeFetches(this.state.filters, this.state.searchText, true);

    this.tableHeadersMap = {
      Screen: current => (
        <VenueSummary
          item={current}
          site={current.site || current.siteInfo}
          showVenueTitle={true}
          showScreenType={false}
          showSerialNumber={false}
          showScreenPlacement={false}
          showArchived={false}
          showNotRegistered={false}
          showTesting={false}
          showExtId={false}
        />
      ),
      'CMS Spots': current => <div>{current.cmsSpots}</div>,
      'OTT Spots': current => <div>{current.ottSpots}</div>,
      'OOH Spots': current => <div>{current.oohSpots}</div>
    };

    this.tableHeaders = Object.keys(this.tableHeadersMap);
    this.onTableValue = this.onTableValue.bind(this);
    this.chartOptions = GraphHelper.getdoughnutChartOptions();
  }

  makeFetches = (filters = this.state.filters, searchText = this.state.searchText, includeOrgSearch = false) => {
    const queryParams = {
      ...filters,
      startDate: this.formatQueryDate(filters.startDate),
      endDate: this.formatQueryDate(filters.endDate)
    };

    if (searchText) {
      queryParams.venueCode = searchText.trim();
    }

    const fetches = [
      {
        method: 'GET',
        paths: ['miscs', 'screen-fill-rate-main'],
        queryParams
      }
    ];

    if (includeOrgSearch) {
      fetches.push({
        method: 'GET',
        paths: ['miscs', 'orgsearch'],
        queryParams: { shallow: true, state: 'LIVE', limit: AppConfig.ORG_SEARCH_LIMIT }
      });
    }

    return fetches;
  };

  formatQueryDate = (value) => {
    if (!value) return '';
    return moment(value).format('YYYY-MM-DD');
  };

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) {
      return '';
    }

    return this.tableHeadersMap[this.tableHeaders[index]](current);
  }

  onActions() {
    return [];
  }

  getStatsItems = () => {
    const stats = this.state.statsTotal || {};

    return [
      {
        title: 'Screens (playing)',
        value: AppHelper.formatNumberStats(stats.screens || 0),
        image: require('../../assets/icons/venueStatus/app-version.svg')
      },
      {
        title: 'Average Online Hours',
        value: stats.avgOnlineHours !== undefined
          ? stats.avgOnlineHours + ' hrs'
          : '-',
        image: require('../../assets/icons/stats/duration.svg')
      },
      {
        title: 'CMS Spots',
        value: AppHelper.formatNumberStats(stats.cmsSpots || 0),
        image: require('../../assets/icons/stats/media.svg')
      },
      {
        title: 'OTT Spots',
        value: AppHelper.formatNumberStats(stats.ottSpots || 0),
        image: require('../../assets/icons/partner-reports/served.svg')
      },
      {
        title: 'OOH Spots',
        value: AppHelper.formatNumberStats(stats.oohSpots || 0),
        image: require('../../assets/icons/venueStatus/orientation.svg')
      }
    ];
  };

  onFetchSuccess(results) {
    const result = results[0] || {};
    const orgSearchResult = results[1] || null;
    const partners = orgSearchResult ? [] : (this.state.partnerOptions || []);
    const sites = orgSearchResult ? [] : (this.state.siteOptions || []);

    if (orgSearchResult) {
      (orgSearchResult.items || []).forEach(org => {
        if (org.groupId === 'PARTNER') {
          partners.push(org);
        } else if (org.groupId === 'SITE') {
          sites.push(org);
        }
      });
      this.hasLoadedOrgSearch = true;
    }

    this.setState({
      statsTotal: result.statsTotal || {},
      statsDay: result.statsDay || {},
      items: result.items || [],
      tableData: result.items || [],
      distribution: result.distribution || {},
      partnerOptions: partners,
      siteOptions: sites,
      inProcessSearch: false
    });
  }

  loadDashboard = () => {
    this.setState({
      inProcessSearch: true
    });
    this.toFetch = this.makeFetches(this.state.filters, this.state.searchText, !this.hasLoadedOrgSearch);
    this.fetchItems();
  };

  isDateRangeWithinLimit = (filters = {}) => {
    const start = moment(filters.startDate)
    const end = moment(filters.endDate)

    if (!start.isValid() || !end.isValid()) {
      return true
    }

    return end.diff(start, 'days', true) <= 7
  }

  clearVenueCodeFromUrl = () => {
    if (
      this.props.history &&
        this.props.location &&
        this.props.location.pathname
    ) {
      this.props.history.replace(this.props.location.pathname);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      const screen = getScreenFromLocation(this.props.location.search);

      if (screen !== this.state.searchText) {
        this.setState({ searchText: screen }, () => {
          this.loadDashboard();
        });
      }
    }
  }

  updateScreenQueryParam = (screen = '') => {
    const { history, location } = this.props;
    if (!history || !location) return;

    const query = queryString.parse(location.search || '');
    delete query.venueCode;
    delete query.ddCode;

    if (screen) {
      query.screen = screen;
    } else {
      delete query.screen;
    }

    const search = queryString.stringify(query);

    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    });
  }

  onSearch = (searchText) => {

    const venueCode = (searchText || '').trim();

    if (!venueCode) {
      this.updateScreenQueryParam('');
    }

    this.setState({
      searchText: venueCode,
    }, () => {
      if (venueCode) {
        this.updateScreenQueryParam(venueCode);
      }
      this.loadDashboard();
    });
  }

  onFilterSubmit = ({ formData, caller }) => {
    if (!this.isDateRangeWithinLimit(formData)) {
      caller.setState({
        success: false,
        message: 'Date range should be within 7 days',
        inProcess: false
      })
      return
    }

    this.setState({
      showFilterDialog:false,
      filters:formData
    },()=>{
      this.loadDashboard();
    });
  }

  getFilterDisplayValue = (key, value) => {
    if (key === 'startDate' || key === 'endDate') {
      return Helper.formatDate2({
        utcTimestamp: value,
        format: 'DD-MMM-YYYY'
      });
    }
    if (key === 'partner') {
      return ((this.state.partnerOptions || []).find(item => item.id === value) || {}).title || value;
    }
    if (key === 'site') {
      return ((this.state.siteOptions || []).find(item => item.id === value) || {}).title || value;
    }
    return value;
  }

  getDistributionChartData = (title, distribution = {}, labels = ['1-99', '100-299', '300+']) => {
    const values = labels.map(label => Number(distribution[label] || 0));
    const colors = ['#3B82F6', '#F59E0B', '#EF4444', '#10B981', '#8B5CF6', '#EC4899'];

    return {
      chartTitle: title,
      data: {
        labels: labels.map((label, index) => `${label} (${values[index]})`),
        datasets: [
          {
            data: values,
            backgroundColor: labels.map((_, index) => colors[index % colors.length]),
            borderColor: labels.map((_, index) => colors[index % colors.length]),
            hoverBackgroundColor: labels.map((_, index) => colors[index % colors.length]),
            borderWidth: 0
          }
        ]
      },
      hasDataMoreThanZero: values.some(value => value > 0)
    };
  }

  getDistributionCharts = () => {
    const distribution = this.state.distribution || {};
    const cmsLabels = ['1-499', '500-999', '1000-1499', '1500-2000', '2000+'];
    const ottOohLabels = ['1-99', '100-199', '200-299', '300-399', '400-499', '500+'];
    const onlineHoursLabels = ['0-10', '11-20', '21-30', '31-40', '41-60', '61-100', '100+'];

    return [
      this.getDistributionChartData('CMS Distribution of Spots', distribution.cms, cmsLabels),
      this.getDistributionChartData('OTT Distribution of Spots', distribution.ott, ottOohLabels),
      this.getDistributionChartData('OOH Distribution of Spots', distribution.ooh, ottOohLabels),
      this.getDistributionChartData('Online Hours', distribution.onlineHours, onlineHoursLabels)
    ];
  }

  renderActiveFilters = () => {

    const filters = this.state.filters;
    const labelMap = {
      startDate: 'Start Date',
      endDate: 'End Date',
      //   venueCode: 'Venue Code',
      partner: 'Partner',
      site: 'Site'
    };

    const entries = Object.entries(filters).filter(([key, value]) => key !== 'REMOVE_ATTRS' && value);

    if(entries.length === 0) {
      return '';
    }

    return (
      <div className={css(Styles.filterChipsRow)}>
        {
          entries.map(([key,value])=>(
            <div
              key={key}
              className={css(Styles.filterChip)}
            >
              {
                labelMap[key] || key }: {
                this.getFilterDisplayValue(key, value)
              }
            </div>
          ))
        }
        <button
          className={css(Styles.filterResetButton)}
          onClick={() => {

            const filters = getDefaultFilters();
            if (this.searchBarComponent) {
              this.searchBarComponent.setState({
                searchText: ''
              });
            }
            this.updateScreenQueryParam('');
            this.setState({
              searchText: '',
              filters
            }, () => {
              this.loadDashboard();
            });
          }}
        >
        Reset Filter
        </button>
      </div>
    );
  }

  renderFilterDialog() {

    if (!this.state.showFilterDialog) {
      return '';
    }

    const formDefinition = JSON.parse(JSON.stringify(screenPerformanceFilterFieldDef.fieldDef));
    UiHelper.populateOptions(formDefinition, { items: this.state.partnerOptions || [] }, 'partner');
    UiHelper.populateOptions(formDefinition, { items: this.state.siteOptions || [] }, 'site');

    Helper.populateFormDefinitionValues(
      formDefinition,
      this.state.filters
    );

    return (
      <DynamicFormDialog
        customSubmit={this.onFilterSubmit}
        formDefinition={formDefinition}
        isOpen={this.state.showFilterDialog}
        onClosed={() =>
          this.setState({
            showFilterDialog:false
          })
        }
      />
    );
  }


  renderAboveTable() {
    const statsItems = this.getStatsItems();
    const statsItemsWithPlaceholder = [
      ...statsItems.slice(0, 2),
      { title: '', value: '' },
      ...statsItems.slice(2)
    ];
    const distributionCharts = this.getDistributionCharts();

    return (
      <div className={css(Styles.renderAboveTable)}>
        <div className={css(Styles.pageContent)}>
          <Grid container spacing={2}>

            <Grid item xs={12}>
              <div className={css(Styles.singleFilterBar)}>
                <div className={css(Styles.searchContainer)}>

                  <SearchBar
                    ref={child => this.searchBarComponent = child}
                    hint='Search by Screen Code'
                    inProcessSearch={this.state.inProcessSearch}
                    callSearchOnEmptyText={true}
                    resetFilterText='Reset Filter'
                    onSearch={this.onSearch}
                    searchText={this.state.searchText}
                    showRecentButtonText='Search'
                  />

                </div>

                <span
                  className={css(Styles.filterIconWrap)}
                  onClick={()=>this.setState({
                    showFilterDialog:true
                  })}
                >

                  <i
                    className='material-icons-outlined'
                    style={{
                      fontSize:20,
                      color:'#020BC3'
                    }}
                  >
                    settings
                  </i>

                </span>

              </div>
              {this.renderActiveFilters()}
              <Stats
                items={statsItemsWithPlaceholder}
                md={4}
                lg={4}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid className={css(Styles.chartsContainer)} container spacing={2}>
                {distributionCharts.map((chart, index) => (
                  <Grid item xs={12} md={4} className={css(Styles.eachChartHolder)} key={chart.chartTitle || index}>
                    <div className={css(Styles.chartOuterWrap)}>
                      <div className={css(Styles.chartBlock)}>
                        <div className={css(Styles.chartTitle)}>{chart.chartTitle}</div>
                        {chart.hasDataMoreThanZero === false
                          ? <div className={css(Styles.noChartMessage)}>No data</div>
                          : (
                            <Doughnut
                              className={css(Styles.chartCanvas)}
                              data={chart.data}
                              options={this.chartOptions}
                              width={100}
                              height={100}
                            />
                          )}
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              {this.renderFilterDialog()}
            </Grid>

          </Grid>
        </div>
      </div>
    );
  }

  renderTable() {
    return (
      <div className={css(Styles.tableBlock)}>
        <h2 className={css(Styles.tableBlockTitle)}>{Helper.getString('screenPerformanceTopScreensTitle')}</h2>
        {super.renderTable()}
      </div>
    )
  }
}


const Styles = StyleSheet.create({
  tableBlock:{
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  tableBlockTitle:{
    padding: '25px 0px 0px',
    marginBottom: 10
  },
  mainContainer: {
    backgroundColor: AppTheme.primaryBg,
    width: '100%',
    '@media (max-width: 1025px)': {
      width: '97vw !important'
    }
  },

  chartsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    height: 'auto',
    width: '100%'
  },
  eachChartHolder: {},
  chartOuterWrap: {
    background: '#fff',
    borderRadius: 16,
    minHeight: 260,
    boxShadow: '0 8px 24px rgba(14, 30, 37, 0.06)'
  },
  chartBlock: {
    height: 220,
    padding: '14px',
    boxSizing: 'border-box'
  },
  chartCanvas: {
    width: '100% !important',
    height: '100% !important'
  },
  noChartMessage: {
    width: '100%',
    minHeight: 180,
    background: '#fff',
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#6b7280'
  },

  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 10,
    color: '#121212'
  },
  filterIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    cursor: 'pointer',
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
    flexShrink: 0,
    overflow: 'hidden'
  },

  singleFilterBar: {
    display: 'flex',
    gap: 10,
    paddingTop: 10,
    paddingBottom: 10
  },

  filterChipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 15,
    marginBottom: 15
  },

  filterChip: {
    backgroundColor: '#fff',
    padding: 10,
    borderRadius: 6,
    fontSize: 14
  },

  filterResetButton: {
    backgroundColor: '#fff',
    padding: '10px 14px',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer',
    color: '#020BC3'
  },
  renderAboveTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    boxSizing: 'border-box',
  },
  pageContent: {
    // boxSizing: 'border-box',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    marginTop: 24,
    marginBottom: 20
  },
  searchContainer:{
    width:'100%',
    flex:1,
    minWidth:0
  }
});



// WEBPACK FOOTER //
// ./src/screens/admin/ScreenPerformanceDashboard.js